import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatRupiah, paymentMethodLabel } from "../lib/money";
import { Link, useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { ReceiptModal } from "../components/ReceiptModal";
import { UsageChart } from "../components/UsageChart";
import { Icon } from "../components/icons";
import { Modal } from "../components/Modal";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  LoadingBlock,
  PageHeader,
  Table,
  Td,
  Th,
  cx,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { track } from "../lib/gtm";
import { absoluteDay, absoluteTime, relativeTime } from "../lib/format";
import { processorName } from "../lib/invoice";
import { PromoCodeField } from "./PromoCodeField";
import { formatMoney, formatRate, formatUsageCost } from "../lib/money";
import {
  useBilling,
  useLedger,
  useUsageHistory,
  useOrg,
  usePayments,
  useBillingDocuments,
  useInvoiceUrl,
  useSpend,
  useBorderPayMethods,
  useStartTopUp,
} from "../lib/queries";
import {
  groupWorkloadsByKind,
  spendDayParts,
  spendKindCount,
  spendKindRows,
} from "../lib/spend";
import type { BillingIdentity,  Payment, Receipt } from "../lib/types";

/* An organization that has filled in nothing. Named rather than inlined so the
   modal is handed the same shape whether or not the query has resolved. */
const EMPTY_BILLING: BillingIdentity = {
  name: null,
  email: null,
  address: null,
  country: null,
  taxId: null,
};

/**
 * What a top-up was worth, carried across the processor's redirect.
 *
 * The approval page is theirs and the customer returns to a fresh document, so
 * the amount they agreed to has to survive outside memory to be reportable as
 * revenue. Session storage keeps it to this tab and this sitting — an abandoned
 * payment must not become a purchase in another tab next week.
 */
const TOPUP_STORAGE_KEY = "maxlayer.topup.pending";

function readPendingTopUp(): { value: number; currency: string } | null {
  try {
    const raw = sessionStorage.getItem(TOPUP_STORAGE_KEY);
    if (!raw) return null;

    // Read field by field rather than trusting the parse: a hand-edited value
    // must not be able to report an arbitrary amount as revenue.
    const parsed: unknown = JSON.parse(raw);
    const record = (parsed ?? {}) as Record<string, unknown>;
    if (typeof record.value !== "number" || typeof record.currency !== "string") return null;
    return { value: record.value, currency: record.currency };
  } catch {
    // Unreadable or unavailable storage. The payment still succeeded; only the
    // amount on the event is lost.
    return null;
  }
}

/** Amounts that cover a useful stretch on the cheapest plans. */
const PRESETS = [1000, 2500, 5000, 10000];

/** Rows per page in each billing history, and how many more each "Show more". */
const HISTORY_PAGE = 25;

/**
 * The footer under a billing history that is only showing part of itself.
 *
 * Renders nothing when the list is complete — a disabled button under a full
 * list is furniture — and says how many are left rather than just "more",
 * because on a billing page the size of the remainder is itself information.
 */
function HistoryFooter({
  shown,
  total,
  loading,
  onMore,
  of,
}: {
  shown: number;
  total: number;
  loading: boolean;
  onMore: () => void;
  /** Which of the three lists this is footing, under `billing.footer`. */
  of: "days" | "receipts" | "adjustments";
}) {
  const { t } = useTranslation("billing");
  if (total <= shown) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line p-2.5">
      {/* The whole sentence per list rather than a count with a noun appended:
          "3 of 40 days" puts the noun where English wants it, which is not
          where every language does. */}
      <p className="pl-2.5 text-xs text-fg-subtle">{t(`footer.${of}`, { shown, total })}</p>
      <Button variant="secondary" size="sm" loading={loading} onClick={onMore}>
        {t("footer.more", { count: Math.min(HISTORY_PAGE, total - shown) })}
      </Button>
    </div>
  );
}

/*
 * The ledger's kinds read `billing.entry.<type>`, and two of those keys are
 * worth explaining where the list used to be.
 *
 * `trial` and `promo` are named for what they are rather than folded into
 * "Adjustment": an adjustment is something an operator decided, and these are
 * credit nobody paid for — a customer looking for the card statement behind one
 * deserves to be told there isn't one. `trial` is the retired automatic grant,
 * kept because the accounts that received it still show it. `expiry` is the
 * other half of an expiring promotion and the one nobody expects, named plainly
 * so a balance that dropped overnight has a line explaining it.
 */

/**
 * A payment the customer can still do something about.
 *
 * Not "anything that is not completed", which is what this card used to show. A
 * cancelled top-up charged nothing and asks nothing of anyone — and the platform
 * now cancels abandoned ones itself, once the processor will no longer let them be
 * approved — so that reading left a card headed "Incomplete payments" that never
 * cleared, presenting months-old dead rows as unfinished business. `pending` is
 * still finishable and `failed` is the one that needs support, which are exactly
 * the two worth a card.
 */
function needsAttention(payment: Payment): boolean {
  // A failed payment support has since settled is finished business, whatever
  // its status still reads.
  return payment.status === "pending" || (payment.status === "failed" && !payment.reconciledAt);
}

/**
 * The most a single top-up may be, matching `startTopUpSchema`'s own ceiling.
 *
 * Enforced here as well as there so the button refuses rather than the API: a
 * customer who has typed a figure and pressed the only button on the dialog
 * should not be told no by a server round trip.
 */
const MAX_TOPUP_CENTS = 10_000_00;

/**
 * What a money field is allowed to contain, and what it is worth in cents.
 *
 * Null rejects the keystroke outright, which is why the pattern is a whitelist
 * rather than a parse. `Number()` accepts a great deal that is not an amount —
 * `1e9` is a hundred million dollars, `-5` is negative, `abc` is `NaN` — and the
 * failure that matters is not a bad value reaching the API. It is the field and
 * the button disagreeing: `Number("abc")` is `NaN`, so a naive parser leaves the
 * previous amount in place, and the dialog then reads "abc" over a button that
 * would cheerfully charge the $25 it still holds.
 *
 * Two decimals because the amount is sent in whole cents, and a third would be
 * silently rounded away after somebody typed it.
 */
export function parseAmountInput(next: string): { text: string; cents: number } | null {
  if (next !== "" && !/^\d*\.?\d{0,2}$/.test(next)) return null;

  const parsed = Number(next);
  return { text: next, cents: Number.isFinite(parsed) ? Math.round(parsed * 100) : 0 };
}

/**
 * Whether a processor adds tax to what the customer chose.
 *
 * True for a merchant of record, which is the seller for what it sells and
 * charges the buyer's VAT or sales tax on top. The platform itself applies no
 * tax handling — Terms §6.2 — so a plain processor charges exactly the figure
 * asked for.
 *
 * Keyed by id rather than sent from the API, deliberately. It describes what a
 * company *is* rather than how this deployment is configured, and there are two
 * of them; a field on the summary would be a third place to keep the same fact
 * in step. Unknown ids answer false, which understates rather than invents a
 * charge nobody was warned about.
 */
/**
 * A payment's reference, short enough for a table column.
 *
 * Stands in for a number on a document we did not number. The full value is on
 * the payment itself and in the modal; this is the part a customer reads out.
 *
 * Rendered bare, with no label saying it is a reference — see the Number column
 * for why that was tried and dropped.
 */
function shortRef(paymentId: string): string {
  return paymentId.slice(0, 8);
}

function chargesTax(providerId: string): boolean {
  return providerId === "polar";
}

export function BillingPage() {
  const { t, i18n } = useTranslation("billing");
  const { currentOrg, user } = useAuth();
  const orgId = currentOrg?.id;
  const canPay = currentOrg?.role === "owner" || currentOrg?.role === "admin";

  /*
   * Three histories, three page sizes, all growing on their own button.
   *
   * They used to be fixed server-side caps with no total: a hundred ledger
   * entries, two hundred usage rows, fifty receipts, and nothing on screen to
   * say the list was a slice. Usage is the one that made it urgent — a row per
   * workload per day means a tenant running four things reached the cap inside
   * two months and simply stopped seeing older charges.
   */
  const [usageLimit, setUsageLimit] = useState(HISTORY_PAGE);
  const [ledgerLimit, setLedgerLimit] = useState(HISTORY_PAGE);
  const [receiptLimit, setReceiptLimit] = useState(HISTORY_PAGE);

  const billing = useBilling(orgId);
  const spend = useSpend(orgId);
  const ledger = useLedger(orgId, ledgerLimit);
  const usage = useUsageHistory(orgId, usageLimit);
  const payments = usePayments(orgId, canPay);
  const org = useOrg(orgId);
  const receipts = useBillingDocuments(orgId, canPay, receiptLimit);
  const invoiceUrl = useInvoiceUrl(orgId ?? "");
  /*
   * Which invoice is being fetched, so its own row spins rather than the table.
   * A merchant of record generates these on demand, so the first click can
   * legitimately answer "not yet" — `invoiceNotice` is how that is said without
   * an error banner over a payment that is perfectly fine.
   */
  const [invoiceFor, setInvoiceFor] = useState<string | null>(null);
  const [invoiceNotice, setInvoiceNotice] = useState<string | null>(null);

  async function openInvoice(paymentId: string) {
    setInvoiceFor(paymentId);
    setInvoiceNotice(null);
    try {
      const { url } = await invoiceUrl.mutateAsync(paymentId);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else setInvoiceNotice(t("invoicePending"));
    } catch (cause) {
      setInvoiceNotice(
        cause instanceof ApiError ? cause.message : t("invoiceFailed"),
      );
    } finally {
      setInvoiceFor(null);
    }
  }
  const startTopUp = useStartTopUp(orgId ?? "");

  const [searchParams, setSearchParams] = useSearchParams();
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [amountCents, setAmountCents] = useState(2500);
  /*
   * What the field shows, kept beside the cents rather than derived from them.
   *
   * Deriving it means the field cannot be cleared or hold "12." on the way to
   * "12.50" — every keystroke is rounded to cents and written back, so a
   * half-typed amount is rewritten under the cursor. The cents stay the value
   * everything else reads; this is only what is on screen.
   */
  const [amountText, setAmountText] = useState("25");
  const [promoOpen, setPromoOpen] = useState(false);
  /*
   * Which processor to pay with, or null for the platform's preferred one.
   *
   * Null rather than the first id, so a deployment that offers one processor
   * sends no choice at all and the API keeps deciding. It also means a customer
   * who never touches the control cannot be pinned to whatever happened to be
   * first when the page loaded.
   */
  const [provider, setProvider] = useState<string | null>(null);
  /** BorderPay's pick: QRIS unless the customer chooses a bank or wallet. */
  const [method, setMethod] = useState<{ kind: "qris" | "va" | "ewallet"; code?: string }>({ kind: "qris" });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);

  // The processor sends the customer back here with the outcome in the query
  // string. Both of them land on the same route: it resolves a state token to a
  // payment row, and that row names who settles it.
  // Read it once, then strip it so a refresh does not replay the banner.
  const toppedUp = searchParams.get("billing_topup");
  const cancelled = searchParams.get("billing_cancelled");
  const callbackError = searchParams.get("billing_error");

  useEffect(() => {
    if (!toppedUp && !cancelled && !callbackError) return;
    if (toppedUp) setNotice(t("paid"));
    if (cancelled) setNotice(t("cancelled"));
    if (callbackError) setError(callbackError);

    /*
     * Revenue, measured where the money actually arrived.
     *
     * The customer comes back to a fresh document, so the amount they
     * agreed to is no longer in memory — it is read back from the note
     * `beginTopUp` left behind. Session storage rather than local: an
     * abandoned top-up should not turn into a purchase event a week later in
     * another tab, and closing the tab is the clearest possible signal that
     * this payment is over.
     *
     * Cleared either way, including on a cancellation, so a second visit to
     * this page cannot report the same payment twice.
     */
    const pending = readPendingTopUp();
    try {
      sessionStorage.removeItem(TOPUP_STORAGE_KEY);
    } catch {
      // Nothing was stored, so nothing needs clearing.
    }

    if (toppedUp) {
      track("purchase", {
        object: "payment",
        id: toppedUp,
        detail: "topup",
        value: pending?.value ?? null,
        currency: pending?.currency ?? null,
      });
    } else if (cancelled) {
      track("checkout_cancel", { object: "payment", detail: "topup" });
    }

    const next = new URLSearchParams(searchParams);
    next.delete("billing_topup");
    next.delete("billing_cancelled");
    next.delete("billing_error");
    setSearchParams(next, { replace: true });
  }, [toppedUp, cancelled, callbackError, searchParams, setSearchParams]);

  /** Takes what was typed, or refuses the keystroke. See `parseAmountInput`. */
  function setAmount(next: string): void {
    const amount = parseAmountInput(next);
    if (!amount) return;
    setAmountText(amount.text);
    setAmountCents(amount.cents);
  }

  async function beginTopUp() {
    setError(null);
    try {
      const { approveUrl } = await startTopUp.mutateAsync({
        amountCents,
        redirectTo: "/settings/billing",
        ...(payingWith && (provider || usingBorderPay) ? { provider: payingWith.id } : {}),
        ...(usingBorderPay ? { method } : {}),
      });

      /*
       * Written before the navigation, not after the mutation resolves into
       * state: the next line leaves this document for the processor's, and anything
       * held in memory goes with it.
       */
      try {
        sessionStorage.setItem(
          TOPUP_STORAGE_KEY,
          JSON.stringify({ value: amountCents / 100, currency }),
        );
      } catch {
        // A browser that will not remember this still has a payment to make.
      }
      track("begin_checkout", {
        object: "payment",
        detail: "topup",
        value: amountCents / 100,
        currency,
      });
      // A full navigation: the payment page is the processor's, not ours.
      window.location.assign(approveUrl);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("paymentFailed"));
      setTopUpOpen(false);
    }
  }

  const summary = billing.data;
  const currency = summary?.currency ?? "USD";
  const low = summary?.lowCredit ?? false;

  /*
   * What this deployment can take money with, and which one this payment will
   * use. The API sends them in its own order of preference, so the first is
   * what an unchosen top-up goes to — naming it here keeps the dialog's copy
   * and the button honest about where somebody is about to be sent.
   *
   * Empty on a deployment with no processor, where the top-up button is not
   * rendered at all.
   */
  const amountInvalid = amountCents < 500 || amountCents > MAX_TOPUP_CENTS;
  /*
   * The processors follow the language. Somebody reading this in Indonesian
   * is paying in rupiah, and BorderPay is the one that takes it, so it is
   * their default with the dollar processors one link away. Everyone else
   * never sees it: a rupiah option on an English page is a currency the
   * customer did not ask about, and the one question this dialog must not
   * raise is "which money is this in".
   */
  const indonesian = i18n.language.startsWith("id");
  const providers = (summary?.paymentProviders ?? []).filter((option) => indonesian || option.id !== "borderpay");
  // Only a default, and only while the customer has not chosen, so a choice
  // is never undone.
  const preferred =
    !provider && indonesian && providers.some((option) => option.id === "borderpay")
      ? "borderpay"
      : provider;
  const payingWith = providers.find((option) => option.id === preferred) ?? providers[0] ?? null;
  const usingBorderPay = payingWith?.id === "borderpay";
  const borderpay = useBorderPayMethods(orgId, usingBorderPay);
  /** The same rounding the API applies, so the estimate is the charge. */
  const rupiah =
    usingBorderPay && borderpay.data
      ? Math.ceil(((amountCents / 100) * borderpay.data.rate) / borderpay.data.roundingIdr) * borderpay.data.roundingIdr
      : null;
  /** Everything except the one being used, offered as a link rather than a field. */
  const alternatives = providers.filter((option) => option.id !== payingWith?.id);

  /*
   * The balance split in two, when part of it is conditional.
   *
   * `spendableOnAiMicros` is the customer's own money and comes from the API
   * rather than being subtracted here — the endpoint computes the same figure
   * for its own headroom, and two subtractions that must agree is one more place
   * for them to stop agreeing.
   */
  const promoShare = useMemo(() => {
    if (!summary || summary.promo.liveMicros <= 0 || summary.balanceMicros <= 0) return null;

    const expiresAt = summary.promo.nextExpiryAt;
    return {
      promoRatio: Math.min(1, summary.promo.liveMicros / summary.balanceMicros),
      yoursMicros: summary.promo.spendableOnAiMicros,
      expiringSoon:
        expiresAt !== null && new Date(expiresAt).getTime() - Date.now() < 7 * 86_400_000,
    };
  }, [summary]);
  const spendData = spend.data;
  const spendError = spend.isError;
  const projected = spendData?.projectedMonthMicros ?? 0;
  const spent = spendData?.monthToDate.totalMicros ?? 0;
  const spendRatio = projected > 0 ? Math.min(1, spent / projected) : 0;

  const kindRows = useMemo(
    () => spendKindRows(spendData?.monthToDate.byKind ?? [], spent),
    [spendData, spent],
  );
  const workloadGroups = useMemo(
    () => groupWorkloadsByKind(spendData?.monthToDate.byWorkload ?? []),
    [spendData],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        action={
          canPay && summary?.topUpAvailable ? (
            <Button icon="plus" onClick={() => setTopUpOpen(true)}>
              {t("addCredit")}
            </Button>
          ) : undefined
        }
      />

      {notice && <Alert tone="success">{notice}</Alert>}
      {error && <Alert>{error}</Alert>}

      {/*
        Named all three, because all three stop. The copy said "apps and
        databases" and left out the half that fails first and loudest: an
        inference call against an empty balance is refused with a 402 the moment
        it is made, while a container keeps serving until the next hourly meter
        runs. A customer reading this while their agent throws errors was told
        about everything except the thing they were looking at.
      */}
      {summary?.suspendedForCredit && (
        <Alert>
          <span className="font-medium">{t("suspendedForCredit.lead")}</span>{" "}
          {t("suspendedForCredit.body")}
          {/*
            The date, once there is one. It is the most important sentence on
            the page from the fourteenth day on, so it is its own line rather
            than a clause at the end of the paragraph above.
          */}
          {summary.creditTerminatedAt ? (
            <span className="mt-2 block font-medium">
              {t("suspendedForCredit.terminated", { date: absoluteDay(summary.creditTerminatedAt) })}
            </span>
          ) : summary.creditTerminatesAt ? (
            <span className="mt-2 block font-medium">
              {t("suspendedForCredit.terminates", { date: absoluteDay(summary.creditTerminatesAt) })}
            </span>
          ) : null}
        </Alert>
      )}

      {/*
        The other suspension, which had no banner at all. An operator's stops
        nothing: the workloads keep running, keep billing, and a top-up does not
        lift it — so it needs the opposite words to the one above, not a share of
        them. Shown only when the credit banner is not, because an organization
        can be under both and two alerts saying different things about the same
        state is worse than the more urgent one alone.
      */}
      {summary?.suspended && !summary.suspendedForCredit && (
        <Alert>
          <span className="font-medium">{t("suspended.lead")}</span> {t("suspended.body")}
          {summary.suspendedReason ? t("suspended.reason", { reason: summary.suspendedReason }) : ""}
          {t("suspended.contact")}
        </Alert>
      )}

      {billing.isLoading ? (
        <LoadingBlock label={t("loading")} />
      ) : !summary ? (
        <Alert>{t("noBalance")}</Alert>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="p-5">
              <p className="text-xs font-medium tracking-wide text-fg-subtle uppercase">
                {t("balance.label")}
              </p>
              <p
                className={cx(
                  "mt-1 text-2xl font-semibold tracking-tight",
                  summary.balanceMicros <= 0
                    ? "text-danger"
                    : "text-fg",
                )}
              >
                {formatMoney(summary.balanceMicros, currency)}
              </p>
              {/*
                A fact about where the balance started, phrased in the past
                tense on purpose. The trial is not ring-fenced — it merged into
                this number and is spent by whatever spends first — so "includes
                $5 trial credit" would be true on day one and quietly false by
                the end of the week. What stays true is that it was granted.
              */}
              {summary.trial.grantedMicros !== null && promoShare === null && (
                <p className="mt-1 text-xs text-fg-subtle">
                  {t("balance.trial", {
                    amount: formatMoney(summary.trial.grantedMicros, currency),
                  })}
                </p>
              )}

              {promoShare && (
                <div className="mt-3 border-t border-line pt-3">
                  <div className="flex h-1.5 overflow-hidden rounded-full bg-raised">
                    <div
                      className="h-full bg-accent"
                      style={{ width: `${promoShare.promoRatio * 100}%` }}
                    />
                  </div>

                  <dl className="mt-2.5 space-y-1.5">
                    <div className="flex items-baseline gap-2">
                      <span
                        aria-hidden="true"
                        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent"
                      />
                      <dt className="text-xs text-fg">
                        {formatMoney(summary.promo.liveMicros, currency)}
                      </dt>
                      <dd className="text-xs text-fg-subtle">{t("balance.promotional")}</dd>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span
                        aria-hidden="true"
                        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-fg-subtle/40"
                      />
                      <dt className="text-xs text-fg">
                        {formatMoney(promoShare.yoursMicros, currency)}
                      </dt>
                      <dd className="text-xs text-fg-subtle">
                        {summary.inferenceUnlocked ? t("balance.yoursWithAi") : t("balance.yours")}
                      </dd>
                    </div>
                  </dl>

                  {summary.promo.nextExpiryAt && (
                    <p
                      className={cx(
                        "mt-2.5 text-[11px] leading-5",
                        promoShare.expiringSoon ? "text-warn" : "text-fg-subtle",
                      )}
                    >
                      Promotional credit expires {absoluteDay(summary.promo.nextExpiryAt)} (
                      {relativeTime(summary.promo.nextExpiryAt)})
                    </p>
                  )}

                  {/*
                    Already worthless, still counted. The sweep runs every five
                    minutes, so this is the window where the balance above is
                    higher than what can be spent — and without saying so, the
                    drop reads as credit going missing.
                  */}
                  {summary.promo.lapsedMicros > 0 && (
                    <p className="mt-1 text-[11px] leading-5 text-warn">
                      {formatMoney(summary.promo.lapsedMicros, currency)} has expired and is about
                      to be removed
                    </p>
                  )}
                </div>
              )}
              {canPay && summary.topUpAvailable && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  icon="plus"
                  onClick={() => setTopUpOpen(true)}
                >
                  {t("addCredit")}
                </Button>
              )}
            </Card>

            <Card className="p-5">
              <p className="text-xs font-medium tracking-wide text-fg-subtle uppercase">
                {t("month.label")}
              </p>
              {/*
                Six places, like every other measurement of usage on this page.
                The month is the sum of app hours, database hours and the inference
                roll-up, so an account whose only workload is tokens has a total
                that sits below a cent — and rounding it to cents reported a real
                charge as $0.01, or as nothing at all.

                The balance above, the receipts and the adjustments stay on cents.
                Those are records of credit moving rather than measurements of what
                was used, and that is the line between the two formatters.
              */}
              <p className="mt-1 text-2xl font-semibold tracking-tight text-fg">
                {spendError ? "—" : spend.isLoading ? "…" : formatUsageCost(spent, currency)}
              </p>
              <p className="mt-0.5 text-xs text-fg-subtle">
                est. {formatUsageCost(projected, currency)} at current pace
              </p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-raised">
                <div
                  className={cx(
                    "h-full rounded-full transition-all",
                    spendRatio >= 1 ? "bg-warn" : "bg-accent",
                  )}
                  style={{ width: `${spendRatio * 100}%` }}
                />
              </div>
            </Card>

            {/*
              The rate, which is a fact, where a projected runway used to be.

              "About 7,616 months left" is what dividing a balance by a near-zero
              burn produces, and it was the headline of this card. The hourly rate
              was already underneath it as supporting detail — it is the more
              honest figure of the two and it is now the tile. Whether somebody is
              about to be stopped is a different question, answered by the banner
              below and by an email, neither of which quotes a horizon.
            */}
            <Card className={cx("p-5", low && "border-warn/30")}>
              <p className="text-xs font-medium tracking-wide text-fg-subtle uppercase">
                {t("burn.label")}
              </p>
              <p
                className={cx(
                  "mt-1 text-2xl font-semibold tracking-tight tabular-nums",
                  low ? "text-warn" : "text-fg",
                )}
              >
                {formatRate(summary.burnPerHourMicros, currency)}
                <span className="text-base font-normal text-fg-subtle">{t("burn.perHour")}</span>
              </p>
              <p className="mt-0.5 text-xs text-fg-subtle">
                {t("burn.perMonth", { amount: formatMoney(summary.burnPerMonthMicros, currency) })}
              </p>
              {/*
                The two halves of that rate, when both exist.

                This figure used to count apps and databases only, because those
                are the things on plans — which meant an account spending most of
                its credit on tokens was told how long it had at a rate that
                ignored them. The split is shown rather than folded away because
                the halves are known differently: one is what the next hour costs
                whether anything happens or not, the other is last week observed,
                and a customer reading a rate deserves to know which they have.
              */}
              {summary.burnBreakdown.inferenceMicros > 0 && (
                <p className="mt-2 border-t border-line pt-2 text-[11px] leading-5 text-fg-subtle">
                  {t("burn.split", {
                    workloads: formatRate(summary.burnBreakdown.workloadsMicros, currency),
                    inference: formatRate(summary.burnBreakdown.inferenceMicros, currency),
                    days: Math.round(summary.burnBreakdown.inferenceWindowHours / 24),
                  })}
                </p>
              )}
            </Card>
          </div>

          {low && !summary.suspendedForCredit && (
            <Alert tone="warning">
              {t("low")}
            </Alert>
          )}

          {/*
            Said here as well as on the AI pages, because this is the screen the
            answer lives on: the customer arriving from a 402 has been told to
            add credit, and the balance in front of them is not empty.

            Gated on having a balance rather than on having had the retired
            trial, which is what it asked before. An account funded entirely by a
            promotion — the ordinary case now — has `trial.grantedMicros` of null,
            so the one notice that explains the 402 never appeared for exactly
            the customers who get it.
          */}
          {!summary.inferenceUnlocked && summary.balanceMicros > 0 && (
            <Alert tone="info">
              <span className="font-medium">{t("inferenceLocked.lead")}</span>{" "}
              {summary.promo.liveMicros > 0
                ? t("inferenceLocked.promo")
                : t("inferenceLocked.granted")}{" "}
              {t("inferenceLocked.unlock")}
            </Alert>
          )}

          {!summary.topUpAvailable && (
            <Alert tone="info">
              {t("noPayments")}
            </Alert>
          )}

          {/*
            The three kinds first, then the workloads inside them.

            This card replaced one flat list of workloads ordered by amount, in
            which an app, a database and the organization's entire token spend
            appeared as peers. The question a customer opens this page with — how
            much of my credit is infrastructure and how much is AI — could only be
            answered by reading every row and grouping it mentally, and the fact
            that two of the three are capped monthly while the third is not could
            not be read at all.
          */}
          <Card>
            <CardHeader
              title={t("thisMonth.title")}
              description={
                spendData
                  ? t("thisMonth.description", {
                      date: new Date(spendData.monthStart).toLocaleDateString(i18n.language, {
                        month: "long",
                        day: "numeric",
                      }),
                    })
                  : t("thisMonth.descriptionNoDate")
              }
            />

            {spendError ? (
              <p className="p-5 text-sm text-fg-subtle">
                {t("thisMonth.failed")}
              </p>
            ) : spend.isLoading ? (
              <LoadingBlock />
            ) : (
              <>
                {/* Two then four, not three: the kinds became four when
                    scheduled jobs went on sale, and a three-column track left
                    the fourth cell alone on a row of its own. */}
                <div className="grid gap-px bg-line sm:grid-cols-2 lg:grid-cols-4">
                  {kindRows.map((row) => (
                    <Link
                      key={row.kind}
                      to={row.to}
                      className="group bg-surface p-5 transition-colors duration-200 ease-smooth hover:bg-raised"
                    >
                      <div className="flex items-center gap-2 text-fg-subtle">
                        <Icon name={row.icon} className="size-3.5" />
                        <span className="text-xs font-medium tracking-wide uppercase">
                          {t(`kind.${row.kind}.label`)}
                        </span>
                      </div>
                      <p className="mt-2 text-xl font-semibold tracking-tight tabular-nums text-fg">
                        {formatUsageCost(row.spentMicros, currency)}
                      </p>
                      {/*
                        The share bar is the comparison this page existed without.
                        Rendered even at zero — as an empty track — so every cell
                        keeps the same height and the eye can read the row as
                        one instrument.
                      */}
                      <div className="mt-3 h-1 overflow-hidden rounded-full bg-raised group-hover:bg-canvas">
                        <div
                          className="h-full rounded-full bg-accent/70"
                          style={{ width: `${row.share * 100}%` }}
                        />
                      </div>
                      <p className="mt-2 flex items-baseline justify-between gap-2 text-[11px] text-fg-subtle">
                        <span>
                          {(() => {
                            const counted = spendKindCount(row);
                            return counted
                              ? t(`kind.${counted.kind}.noun`, { count: counted.count })
                              : t(`meter.${row.meter}`);
                          })()}
                        </span>
                        <span className="tabular-nums">
                          {row.share > 0 ? `${Math.round(row.share * 100)}%` : "—"}
                        </span>
                      </p>
                    </Link>
                  ))}
                </div>

                {workloadGroups.length === 0 ? (
                  <p className="border-t border-line p-5 text-sm text-fg-subtle">
                    {t("thisMonth.empty")}
                  </p>
                ) : (
                  <ul className="divide-y divide-line border-t border-line">
                    {workloadGroups.map((group) => (
                      <li key={group.meta.kind}>
                        <div className="flex items-baseline justify-between gap-4 bg-raised px-5 py-2">
                          <span className="text-xs font-medium tracking-wide text-fg-subtle uppercase">
                            {t(`kind.${group.meta.kind}.label`)}
                          </span>
                          <span className="text-xs tabular-nums text-fg-subtle">
                            {t(`meter.${group.meta.meter}`)}
                          </span>
                        </div>
                        <ul className="divide-y divide-line">
                          {group.workloads.map((w, index) => (
                            <li key={`${w.kind}-${w.id ?? index}`} className="px-5 py-3">
                              <div className="flex items-baseline justify-between gap-4">
                                <p className="min-w-0 truncate text-sm text-fg">
                                  {w.name ?? (
                                    <span className="text-fg-subtle">deleted {w.kind}</span>
                                  )}
                                  {/* Inference has no plan, and an em dash in the
                                      slot where a tier name goes reads as a
                                      missing value rather than as a different
                                      billing model. */}
                                  {w.kind !== "inference" && (
                                    <span className="ml-2 text-xs text-fg-subtle">
                                      {w.planName ?? "—"}
                                    </span>
                                  )}
                                </p>
                                <p className="shrink-0 text-sm tabular-nums text-fg">
                                  {formatUsageCost(w.spentMicros, currency)}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </Card>

          {/*
            Its own card, because it is a different window. The chart has always
            been the trailing 30 days while the breakdown above is month-to-date,
            and the two sat inside one card under one heading — so on the third of
            the month a reader saw a full chart above a nearly empty list and had
            nothing on screen to explain it.
          */}
          {!spendError && (
            <Card>
              <CardHeader
                title={t("chart.title")}
                description={t("chart.description")}
              />
              <div className="p-5">
                <UsageChart
                  points={(spendData?.days ?? []).map((day) => ({
                    day: day.day,
                    value: day.totalMicros,
                    /* The chart is shared with the AI page and takes labelled
                       parts, so the kinds are named here rather than there. */
                    parts: spendDayParts(day).map((part) => ({
                      label: t(`kind.${part.kind}.label`),
                      value: part.value,
                    })),
                  }))}
                  format={(value) => formatUsageCost(value, currency)}
                  label={t("chart.label")}
                  emptyLabel={t("chart.empty")}
                />
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title={t("history.title")} description={t("history.description")} />
            {usage.isLoading ? (
              <LoadingBlock />
            ) : (usage.data?.days ?? []).length === 0 ? (
              <p className="p-5 text-sm text-fg-subtle">
                {t("history.empty")}
              </p>
            ) : (
              <Table
                head={
                  <tr>
                    <Th>{t("history.day")}</Th>
                    <Th>{t("history.workload")}</Th>
                    <Th priority="sm">{t("history.kind")}</Th>
                    <Th className="text-right">{t("history.charge")}</Th>
                  </tr>
                }
              >
                {(usage.data?.days ?? []).map((row, index) => (
                  <tr key={`${row.day}-${row.kind}-${row.workloadId ?? index}`}>
                    <Td>{new Date(row.day).toLocaleDateString(i18n.language)}</Td>
                    <Td>
                      {row.workloadName ?? (
                        <span className="text-fg-subtle">{t("history.deleted")}</span>
                      )}
                    </Td>
                    {/* The column is headed "Kind" and printed the meter —
                        "hourly" or "per token" — which is a different fact and one
                        the workload name mostly gives away anyway. It names the
                        kind now, in the same words the breakdown above uses, so a
                        reader can filter this table by eye against that one. */}
                    <Td priority="sm" className="text-fg-subtle">
                      {t(`kind.${row.kind}.label`)}
                    </Td>
                    {/* A day of token spend belongs to the same column as a day
                        of app hours, and at two decimal places a light day of it
                        printed $0.00 — a charge that reads as no charge, in the
                        table a customer opens to find out what they were
                        charged. */}
                    <Td className="text-right tabular-nums">
                      {formatUsageCost(Math.abs(row.amountMicros), currency)}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}

            <HistoryFooter
              shown={(usage.data?.days ?? []).length}
              total={usage.data?.total ?? 0}
              loading={usage.isPlaceholderData}
              onMore={() => setUsageLimit((current) => current + HISTORY_PAGE)}
              of="days"
            />
          </Card>

          <Card>
            <CardHeader
              title={t("receipts.title")}
              description={t("receipts.description")}
            />
            {!canPay ? (
              <p className="p-5 text-sm text-fg-subtle">
                {t("receipts.noAccess")}
              </p>
            ) : receipts.isLoading ? (
              <LoadingBlock />
            ) : (receipts.data?.documents.length ?? 0) === 0 ? (
              /*
                Not "a receipt appears the moment a top-up completes", which
                stopped being true when a merchant of record started taking
                payments. Polar is the seller for what it sells, charges the tax
                and issues the invoice for it, so those top-ups produce no
                receipt here — and somebody who has just paid and found this list
                empty needs to be told where theirs is rather than left to
                conclude the payment failed.
              */
              <p className="p-5 text-sm text-fg-subtle">{t("receipts.empty")}</p>
            ) : (
              <Table
                head={
                  <tr>
                    {/* "Code", not "Number". Ours are numbered `ML-2026-0001`
                        from `receipt_sequences` and a merchant of record's row
                        carries the seller's own invoice number, but a row the
                        seller has not numbered yet falls back to a payment
                        reference, and a header that says Number is wrong for
                        that one. Code is true of all of them. */}
                    <Th>{t("receipts.code")}</Th>
                    <Th priority="sm">{t("receipts.date")}</Th>
                    <Th priority="md">{t("receipts.method")}</Th>
                    <Th className="text-right">{t("receipts.amount")}</Th>
                    <Th className="text-right"></Th>
                  </tr>
                }
              >
                {receipts.data?.documents.map((document) => (
                  <tr key={document.id}>
                    {/*
                      The number printed on the document, whoever issued it:
                      ours from our sequence on a receipt, the seller's own on a
                      merchant of record's invoice — the API stores Polar's
                      `invoice_number` on the payment and serves it here. Both
                      kinds of row carry a real number, in one monospace column,
                      so the table reads as one list rather than two kinds
                      bolted together.

                      The fallback is the payment reference the modal calls
                      "Payment reference": the thing a customer quotes to
                      support. It shows only on an invoice the seller has not
                      numbered yet, or one settled before the number was
                      recorded — opening that invoice once backfills it.

                      Deliberately bare. Both kinds were labelled for a while —
                      `receipt ML-2026-0001` over `ref a6ecb726`, fixed-width so
                      the identifiers aligned — and it was dropped on review: the
                      label repeated on every row what the panel heading and the
                      Method column beside it already say, and paid for it in the
                      width of the one column somebody scans down. The header
                      carries that job instead, which is cheaper and truer: see
                      `Code` above.

                      What must never happen is minting an `ML-` number for a
                      seller's row: that sequence numbers documents we issue as
                      seller, and a second number on a sale somebody else made
                      contradicts theirs.
                    */}
                    <Td className="ident">{document.number ?? shortRef(document.paymentId)}</Td>
                    <Td priority="sm">{absoluteTime(document.issuedAt)}</Td>
                    {/* Not CSS `capitalize`, which renders "Paypal". Same
                        spelling as the document itself uses. */}
                    <Td priority="md">
                      {processorName(document.provider)}
                      {/* What was actually paid, when the processor charged
                          another currency: the figure the bank statement shows. */}
                      {document.charged && (
                        <p className="text-xs text-fg-subtle">
                          {formatRupiah(document.charged.amount)}
                          {(() => {
                            const label = paymentMethodLabel(document.charged.method, (bank) =>
                              bank ? t("topUp.borderpay.va", { bank }) : t("topUp.borderpay.kind.va"),
                            );
                            return label ? ` · ${label}` : "";
                          })()}
                        </p>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums">
                      {formatMoney(document.amountMicros, document.currency)}
                    </Td>
                    <Td className="text-right">
                      {document.kind === "receipt" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setSelectedReceipt({ ...document, number: document.number ?? "" })
                          }
                        >
                          {t("receipts.view")}
                        </Button>
                      ) : (
                        /*
                          The same label, because it is the same intent: see the
                          document for this payment. The arrow is what differs,
                          and it is the one this app already uses for a link that
                          leaves — the seller's invoice is on their site.
                        */
                        <Button
                          variant="ghost"
                          size="sm"
                          icon="arrow-up-right"
                          loading={invoiceFor === document.paymentId}
                          onClick={() => void openInvoice(document.paymentId)}
                        >
                          {t("receipts.view")}
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}

            {/*
              Said under the table rather than as a banner over the page: a
              seller generating an invoice on demand is not an error, and the
              row it belongs to is right here.
            */}
            {invoiceNotice && (
              <p className="border-t border-line px-5 py-3 text-xs text-fg-subtle">
                {invoiceNotice}
              </p>
            )}

            {canPay && (
              <HistoryFooter
                shown={(receipts.data?.documents ?? []).length}
                total={receipts.data?.total ?? 0}
                loading={receipts.isPlaceholderData}
                onMore={() => setReceiptLimit((current) => current + HISTORY_PAGE)}
                of="receipts"
              />
            )}
          </Card>

          <Card>
            <CardHeader
              title={t("adjustments.title")}
              description={t("adjustments.description")}
            />
            {(ledger.data?.entries ?? []).length === 0 ? (
              <p className="p-5 text-sm text-fg-subtle">{t("adjustments.empty")}</p>
            ) : (
              <Table
                head={
                  <tr>
                    <Th priority="sm">{t("adjustments.when")}</Th>
                    <Th priority="md">{t("adjustments.type")}</Th>
                    <Th>{t("adjustments.note")}</Th>
                    <Th className="text-right">{t("adjustments.amount")}</Th>
                  </tr>
                }
              >
                {/* No client-side filter any more: the endpoint excludes
                    top-ups, so the page it returns is a page of this table
                    rather than a page of something this table shows part of. */}
                {(ledger.data?.entries ?? []).map((entry) => (
                  <tr key={entry.id}>
                    <Td priority="sm">{relativeTime(entry.createdAt)}</Td>
                    <Td priority="md">{t(`entry.${entry.type}`)}</Td>
                    <Td className="text-fg-subtle">
                      <span>{entry.description ?? "—"}</span>
                      <span className="mt-1 block text-xs text-fg-subtle sm:hidden">
                        {t(`entry.${entry.type}`)} · {relativeTime(entry.createdAt)}
                      </span>
                    </Td>
                    <Td
                      className={cx(
                        "text-right tabular-nums",
                        entry.amountMicros > 0 ? "text-mint" : "text-fg",
                      )}
                    >
                      {entry.amountMicros > 0 ? "+" : "−"}
                      {formatMoney(Math.abs(entry.amountMicros), currency)}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}

            <HistoryFooter
              shown={(ledger.data?.entries ?? []).length}
              total={ledger.data?.total ?? 0}
              loading={ledger.isPlaceholderData}
              onMore={() => setLedgerLimit((current) => current + HISTORY_PAGE)}
              of="adjustments"
            />
          </Card>

          {canPay && (payments.data ?? []).some(needsAttention) && (
            <Card>
              <CardHeader title={t("incomplete.title")} />
              <Table
                head={
                  <tr>
                    <Th>{t("incomplete.when")}</Th>
                    <Th>{t("incomplete.amount")}</Th>
                    <Th>{t("incomplete.status")}</Th>
                  </tr>
                }
              >
                {(payments.data ?? [])
                  .filter(needsAttention)
                  .map((payment) => (
                    <tr key={payment.id}>
                      <Td>{relativeTime(payment.createdAt)}</Td>
                      <Td>{formatMoney(payment.amountMicros, payment.currency)}</Td>
                      <Td className="text-fg-subtle">
                        {/*
                          A parked payment's reason is the operator's note to
                          themselves ("settle with an adjustment"). The
                          customer's money arrived and a person is on it, and
                          that is the sentence they need.
                        */}
                        {payment.parked ? (
                          t("incomplete.parked")
                        ) : (
                          <>
                            {payment.status}
                            {payment.failureReason && ` — ${payment.failureReason}`}
                          </>
                        )}
                      </Td>
                    </tr>
                  ))}
              </Table>
            </Card>
          )}
        </>
      )}

      <Modal
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        title={t("topUp.title")}
      >
        <div className="space-y-4">
          {/*
            One value, shown once.

            The presets and the field used to display the same number in two
            controls — $25.00 lit up above a box reading 25 — which reads as two
            answers to one question. The field is the amount now, big enough to
            be read as the thing being decided, and the presets under it are
            shortcuts that fill it.
          */}
          <div>
            <label htmlFor="amount" className="micro text-fg-subtle">
              {t("topUp.amount")}
            </label>
            <div className="relative mt-1.5">
              {/*
                The currency inside the field rather than in a label beside it.
                A money input with no unit in it is the one place somebody types
                a number meaning something other than what we credit.
              */}
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-lg text-fg-subtle">
                $
              </span>
              {/*
                `inputMode` rather than `type="number"`. Number renders spinner
                arrows nobody wants on a payment amount, and — the real hazard —
                a scroll over a focused field silently changes what is about to
                be charged.
              */}
              <input
                id="amount"
                inputMode="decimal"
                autoComplete="off"
                value={amountText}
                onChange={(event) => setAmount(event.target.value)}
                onBlur={() => setAmountText(String(amountCents / 100))}
                aria-invalid={amountInvalid ? true : undefined}
                className={cx(
                  "h-12 w-full rounded-md border bg-panel pl-7 pr-3.5 text-lg tabular-nums text-fg",
                  "transition-colors duration-200 ease-smooth outline-none",
                  "focus:border-accent focus:ring-2 focus:ring-accent/20",
                  amountInvalid ? "border-danger/50" : "border-line",
                )}
              />
            </div>

            <div className="mt-2 flex flex-wrap gap-1.5">
              {PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmount(String(preset / 100))}
                  className={cx(
                    "rounded-md border px-2.5 py-1 text-xs font-medium tabular-nums",
                    "transition-colors duration-200 ease-smooth",
                    amountCents === preset
                      ? "border-accent/50 bg-accent-soft text-accent"
                      : "border-line text-fg-subtle hover:border-line-strong hover:text-fg",
                  )}
                >
                  {formatMoney(preset * 10_000, currency)}
                </button>
              ))}
            </div>

            <p className={cx("mt-2 text-xs", amountInvalid ? "text-danger" : "text-fg-subtle")}>
              {amountCents > MAX_TOPUP_CENTS
                ? t("topUp.maximum", { amount: formatMoney(MAX_TOPUP_CENTS * 10_000, currency) })
                : summary && summary.burnPerHourMicros > 0 && amountCents >= 500
                  ? t("topUp.minimumWithDays", {
                      amount: "$5",
                      count: Math.floor((amountCents * 10_000) / summary.burnPerHourMicros / 24),
                    })
                  : t("topUp.minimum", { amount: "$5" })}
            </p>
          </div>

          {/*
            Folded away until asked for. It is the least-used control in the
            dialog and it sat between the amount and the action, so most people
            read past a labelled field, an input and a button to reach the thing
            they came for. Anybody holding a code is looking for it.

            Still with the amount rather than with the payment: applying is
            independent of paying — a grant credits on the spot and the dialog
            can be closed without paying at all — but both change what this
            top-up is worth.
          */}
          {orgId &&
            (promoOpen ? (
              <PromoCodeField orgId={orgId} currency={currency} />
            ) : (
              <button
                type="button"
                onClick={() => setPromoOpen(true)}
                className="text-xs text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
              >
                {t("topUp.promo")}
              </button>
            ))}

          {/*
            The processor is not a form field.

            It was a labelled section with two equal cards, each carrying a
            caption — which is the shape for a decision somebody has to make.
            This one has a default: Polar is preferred because it is a merchant
            of record and handles the tax the platform never has. So the button
            names where you are going, and the alternative is one link.

            The only thing that still has to be said is the money: Polar adds
            tax to what you pay, and finding that out on their page — after
            committing, on somebody else's site — is a refund request. Nothing
            is said for a processor that adds nothing, because there is no
            surprise to warn about.
          */}
          {/*
            BorderPay asks the method up front -- a QR, a virtual account at a
            bank, a wallet -- and the host publishes which are on. Drawn from
            its list, never a hardcoded set.

            Compact by design: the dialog already holds the amount, and a
            method picker that lists every bank and wallet with a sentence
            each pushed the button below the fold. So one row of three
            segments, one line under it that explains only the segment in
            hand -- what to do next, how long it stays valid, and the banks
            or wallets to pick from where there is more than one -- and then
            the money: the amount above is the credit, the total here is
            what leaves the bank account, and the button says the same
            number so nobody commits to a figure they have not seen.
          */}
          {usingBorderPay && (
            <div className="space-y-2.5 border-t border-line pt-3">
              {borderpay.isPending ? (
                <p className="text-xs text-fg-subtle">{t("topUp.borderpay.loading")}</p>
              ) : borderpay.error ? (
                <p className="text-xs text-danger">{t("topUp.borderpay.unavailable")}</p>
              ) : (
                (() => {
                  const kinds = (["qris", "va", "ewallet"] as const).filter((kind) =>
                    (borderpay.data?.methods ?? []).some((option) => option.method === kind),
                  );
                  const optionsFor = (kind: (typeof kinds)[number]) =>
                    (borderpay.data?.methods ?? []).filter((option) => option.method === kind);
                  const current = kinds.includes(method.kind) ? method.kind : kinds[0];
                  const options = current ? optionsFor(current) : [];
                  const single = options.length === 1 ? options[0] : null;
                  const pick = (option: (typeof options)[number]) =>
                    setMethod(option.code ? { kind: option.method, code: option.code } : { kind: option.method });
                  return (
                    <>
                      <div
                        role="radiogroup"
                        aria-label={t("topUp.borderpay.method")}
                        className="grid gap-1.5"
                        style={{ gridTemplateColumns: `repeat(${kinds.length}, minmax(0, 1fr))` }}
                      >
                        {kinds.map((kind) => {
                          const active = kind === current;
                          const own = optionsFor(kind);
                          return (
                            <button
                              key={kind}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              onClick={() => pick(own.find((option) => option.code === method.code) ?? own[0]!)}
                              className={cx(
                                "rounded-md border px-2 py-2 text-center text-xs transition-colors duration-200 ease-smooth",
                                active
                                  ? "border-accent bg-accent/10 text-fg"
                                  : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
                              )}
                            >
                              {kind === "qris"
                                ? "QRIS"
                                : kind === "va" && own.length === 1
                                  ? t("topUp.borderpay.va", { bank: own[0]!.label })
                                  : t(`topUp.borderpay.kind.${kind as "va" | "ewallet"}`)}
                            </button>
                          );
                        })}
                      </div>
                      {current && (
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                          <p className="text-xs leading-5 text-fg-subtle">
                            {t(`topUp.borderpay.hint.${current}`)}{" "}
                            <span className="whitespace-nowrap">· {t(`topUp.borderpay.valid.${current}`)}</span>
                          </p>
                          {!single && (
                            <div className="flex flex-wrap gap-1.5" role="group" aria-label={t(`topUp.borderpay.kind.${current as "va" | "ewallet"}`)}>
                              {options.map((option) => {
                                const chosen = option.code === method.code;
                                return (
                                  <button
                                    key={option.code ?? option.label}
                                    type="button"
                                    aria-pressed={chosen}
                                    onClick={() => pick(option)}
                                    className={cx(
                                      "rounded-md border px-2 py-0.5 text-xs transition-colors duration-200 ease-smooth",
                                      chosen ? "border-accent bg-accent/10 text-fg" : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
                                    )}
                                  >
                                    {option.label}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()
              )}
              {rupiah !== null && borderpay.data && (
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 border-t border-line pt-2.5">
                  <span className="text-xs text-fg-muted">{t("topUp.borderpay.total")}</span>
                  <span className="text-sm font-medium tabular-nums text-fg">{formatRupiah(rupiah)}</span>
                  <span className="basis-full text-[11px] leading-4 text-fg-subtle">
                    {t("topUp.borderpay.rate", { rate: formatRupiah(borderpay.data.rate), rounding: formatRupiah(borderpay.data.roundingIdr) })}
                  </span>
                </div>
              )}
            </div>
          )}

          {payingWith && (chargesTax(payingWith.id) || alternatives.length > 0) && (
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line pt-3 text-xs text-fg-subtle">
              <span>
                {chargesTax(payingWith.id) ? t("topUp.tax") : ""}
              </span>
              <span className="flex gap-3">
                {alternatives.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setProvider(option.id)}
                    className="text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
                  >
                    {t("topUp.payWith", { provider: option.label })}
                  </button>
                ))}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setTopUpOpen(false)}>
              {t("topUp.cancel")}
            </Button>
            <Button
              loading={startTopUp.isPending}
              disabled={amountInvalid}
              onClick={() => void beginTopUp()}
            >
              {/*
                What the customer is doing, not who processes it. It named the
                processor while the choice was a form field and both options
                were equal; with a preferred one that reads as branding for a
                company they did not choose to deal with. The arrow still says
                this leaves the dashboard, and the link beside it names the
                alternative for anyone who wants a different one.
              */}
              {usingBorderPay && rupiah !== null ? t("topUp.pay", { amount: formatRupiah(rupiah) }) : t("topUp.submit")}
              <Icon name="arrow-up-right" className="size-4" />
            </Button>
          </div>
        </div>
      </Modal>

      <ReceiptModal
        receipt={selectedReceipt}
        onClose={() => setSelectedReceipt(null)}
        issuer={receipts.data?.issuer ?? null}
        /* From the org query rather than from `currentOrg`: the switcher's copy
           is the session's, which is not refetched when the billing details are
           saved, so an invoice downloaded right after saving would carry the
           identity from before. */
        customer={org.data?.billing ?? EMPTY_BILLING}
        fallbackEmail={user?.email ?? null}
      />
    </div>
  );
}
